import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	styled,
} from "@mui/material";
import { api } from "api";
import { Activity, ActivityAdd, Assignment, UUID } from "api/types";
import { AssignmentDetailDialog } from "components/AssignmentDetailDialog";
import { InfoSnackBar } from "components/InfoSnackBar";
import { SearchActivities } from "components/SearchActivities";
import { ERR_CREATE } from "messages";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "CreateAssignmentPage";

const classes = {
	dialog: `${PREFIX}-dialog`,
	content: `${PREFIX}-content`,
	gridContent: `${PREFIX}-gridContent`,
	action: `${PREFIX}-action`,
	textField: `${PREFIX}-textField`,
	label: `${PREFIX}-label`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.dialog}`]: {
		padding: theme.spacing(2),
	},

	[`& .${classes.content}`]: {
		padding: theme.spacing(2),
	},

	[`& .${classes.gridContent}`]: {
		marging: theme.spacing(2),
	},

	[`& .${classes.action}`]: {
		margin: 0,
		padding: theme.spacing(1),
	},

	[`& .${classes.textField}`]: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},

	[`& .${classes.label}`]: {
		backgroundColor: "white",
		padding: theme.spacing(0, 1),
	},
}));

export interface ParamType {
	assignmentId: UUID;
}
export interface CreateAssignmentPageProps {
	mode: "create" | "edit";
}

export const CreateAssignmentPage = (
	props: CreateAssignmentPageProps,
): JSX.Element => {
	const { mode } = props;
	const params = useParams<ParamType>();
	const [open, setOpen] = useState<boolean>(false);
	const [activities, setActivities] = useState<Activity[]>([]);
	const [err, setErr] = useState<boolean>(false);
	const [openConfirmAdd, setOpenConfirmAdd] = useState<boolean>(false);
	const assignment: Assignment = {
		assignmentId: "",
		groupId: "",
		activities: [],
		startDate: "",
		endDate: "",
		name: "",
		status: "Open",
	};

	const history = useHistory();

	const handleOpen = (activityList: Activity[]): void => {
		setActivities(activityList);
		setOpen(true);
	};

	const handleAddActivity = (activityList: Activity[]): void => {
		setActivities(activityList);
		setOpenConfirmAdd(true);
	};

	const handleClose = (): void => {
		setOpen(false);
		setOpenConfirmAdd(false);
	};

	const onConfirmAdd = (): void => {
		onAddActivities()
			.then(() => {
				history.push(
					routes._.assignments._.view.path.replace(
						":assignmentId",
						params.assignmentId,
					),
				);
			})
			.catch(() => {
				setErr(true);
			});
	};

	const onAddActivities = async (): Promise<void> => {
		const activitiesAdd: ActivityAdd[] = [];
		activities.forEach((item) => {
			activitiesAdd.push({
				questId: item.questId,
				activityType: item.activityType,
			});
		});
		const assignmentUUID: UUID = params.assignmentId;

		try {
			const res = await api.addActivitiesToAssignment(
				assignmentUUID,
				activitiesAdd,
			);
			if (res.status !== 200) {
				setErr(true);
				setOpenConfirmAdd(false);
				throw new Error();
			}
			setOpen(false);
		} catch (e) {
			setErr(true);
		}
	};

	return (
		<Root>
			<SearchActivities
				mode={mode}
				activitiesHandler={mode === "edit" ? handleAddActivity : handleOpen}
			/>
			<AssignmentDetailDialog
				open={open}
				mode="create"
				detail={assignment}
				activities={activities}
				handleClose={handleClose}
			/>
			<Dialog
				onClose={handleClose}
				open={openConfirmAdd}
				className={classes.dialog}
				maxWidth="xs"
			>
				<DialogTitle>Add Activities</DialogTitle>
				<DialogContent dividers>
					Do you want to add new activities to this assignment?
				</DialogContent>
				<DialogActions className={classes.action}>
					<Button data-testid="cancel-button" onClick={handleClose}>
						Cancel
					</Button>
					<Button color="primary" data-testid="crt-btn" onClick={onConfirmAdd}>
						CONFIRM
					</Button>
				</DialogActions>
			</Dialog>
			<InfoSnackBar
				isOpen={err}
				severity="error"
				onClose={() => setErr(false)}
				message={ERR_CREATE}
			/>
		</Root>
	);
};
